import React, { useState, useEffect } from "react";
import "./Register.css";
import { Link, useNavigate } from "react-router-dom";

// State and city data for USA and India
const countryData = {
  US: {
    name: "United States",
    states: {
      CA: {
        name: "California",
        cities: ["San Jose"]
      },
    }
  },
  IN: {
    name: "India",
    states: {
      KA: {
        name: "Karnataka",
        cities: ["Udupi", "Kundapura", "Karkala", "Byndoor", "Brahmavar", "Hebri", "Kaup"]
      }
    }
  }
};

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [extraInfo, setExtraInfo] = useState({
    first_name: "",
    last_name: "",
    address: "",
    contact_number: "",
    user_country: "",
    user_state: "",
    user_city: ""
  });
  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtraLoading, setIsExtraLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+91"); // Default to India
  
  // Country, state, city dropdown data
  const [countries] = useState([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" }
  ]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleExtraChange = (e) => {
    const { name, value } = e.target;
    setExtraInfo(prev => {
      const newInfo = { ...prev, [name]: value };
      
      // Reset dependent fields when country or state changes
      if (name === "user_country") {
        newInfo.user_state = "";
        newInfo.user_city = "";
      } else if (name === "user_state") {
        newInfo.user_city = "";
      }
      
      return newInfo;
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  // Update states when country changes
  useEffect(() => {
    if (extraInfo.user_country && countryData[extraInfo.user_country]) {
      const countryStates = Object.entries(countryData[extraInfo.user_country].states).map(
        ([code, state]) => ({ code, name: state.name })
      );
      setStates(countryStates);
    } else {
      setStates([]);
      setCities([]);
    }
  }, [extraInfo.user_country]);

  // Update cities when state changes
  useEffect(() => {
    if (extraInfo.user_country && extraInfo.user_state && 
        countryData[extraInfo.user_country]?.states[extraInfo.user_state]) {
      setCities(countryData[extraInfo.user_country].states[extraInfo.user_state].cities);
    } else {
      setCities([]);
    }
  }, [extraInfo.user_state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, email, password } = formData;

    if (!username || !email || !password) {
      setError("All fields are required!");
      setSuccess("");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/user/register_api`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setError("");
        setSuccess("Registration successful!");
        setRegisteredUserId(data.userID);
        setTimeout(() => {
          setShowExtraForm(true);
          setSuccess("");
        }, 1200);
      } else {
        const errorMsg = data.error || "Registration failed!";
        setError(errorMsg);
        setSuccess("");
        
        if (errorMsg.includes("Username")) {
          setFormData(prev => ({ ...prev, username: "" }));
        }
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      setSuccess("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkUsername = async () => {
      if (formData.username.length > 3) {
        try {
          const response = await fetch(`${API_URL}/user/check_username`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ username: formData.username }),
          });
          const data = await response.json();
          if (data.available === false) {
            setError("Username is already taken");
          } else if (error === "Username is already taken") {
            setError("");
          }
        } catch (err) {
          console.error("Error checking username:", err);
        }
      }
    };

    const timer = setTimeout(() => {
      if (formData.username) checkUsername();
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  const handleExtraSubmit = async (e) => {
    e.preventDefault();
    setIsExtraLoading(true);

    // Create FormData object
    const formData = new FormData();
    formData.append('userid', registeredUserId);
    formData.append('first_name', extraInfo.first_name);
    formData.append('last_name', extraInfo.last_name);
    formData.append('address', extraInfo.address);
    formData.append('contact_number', countryCode + extraInfo.contact_number);
    formData.append('user_country', extraInfo.user_country);
    formData.append('user_state', extraInfo.user_state);
    formData.append('user_city', extraInfo.user_city);
    
    // Append profile image if selected
    if (profileImage) {
      formData.append('profile_image', profileImage);
    }

    try {
      const response = await fetch(`${API_URL}/user/save_info_initial`, {
        method: "POST",
        body: formData,
        // Don't set Content-Type header - let the browser set it with boundary
      });

      const data = await response.json();
      if (response.ok) {
        setError("");
        setSuccess("Account setup complete! Redirecting...");
        setTimeout(() => navigate("/"), 1500);
      } else {
        setError(data.error || "Failed to save information!");
        setSuccess("");
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      setSuccess("");
    } finally {
      setIsExtraLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        {!showExtraForm ? (
          <>
            <div className="welcome-header">
              <h1>Create Your Account</h1>
            </div>
            <form onSubmit={handleSubmit} className="register-form">
              <div className="input-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                />
              </div>
              <div className="input-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
              </div>
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              {success && <p className="success-message">{success}</p>}
              <button 
                type="submit" 
                className="register-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="button-spinner"></span>
                ) : (
                  "Create Account"
                )}
              </button>
              <div className="login-options">
                <p className="login-link">
                  Already have an account? <Link to="/">Sign in</Link>
                </p>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="welcome-header">
              <h1>Complete Your Profile</h1>
              <p className="welcome-subtitle">Tell us more about yourself</p>
            </div>
            <form onSubmit={handleExtraSubmit} className="register-form">
              <div className="input-group">
                <label htmlFor="profileImage">Profile Image (Optional)</label>
                <input
                  type="file"
                  id="profileImage"
                  name="profile_image"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </div>
              <div className="input-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={extraInfo.first_name}
                  onChange={handleExtraChange}
                  placeholder="Your first name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={extraInfo.last_name}
                  onChange={handleExtraChange}
                  placeholder="Your last name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={extraInfo.address}
                  onChange={handleExtraChange}
                  placeholder="Your address"
                />
              </div>
              <div className="input-group">
                <label htmlFor="contact_number">Contact Number</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    style={{ marginRight: 8, padding: 4 }}
                    required
                  >
                    <option value="+91">🇮🇳 +91 (India)</option>
                    <option value="+1">🇺🇸 +1 (USA)</option>
                  </select>
                  <input
                    type="text"
                    id="contact_number"
                    name="contact_number"
                    value={extraInfo.contact_number}
                    onChange={handleExtraChange}
                    placeholder="Phone number"
                    required
                    maxLength={10}
                    pattern="\d{10}"
                  />
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="user_country">Country</label>
                <select
                  id="user_country"
                  name="user_country"
                  value={extraInfo.user_country}
                  onChange={handleExtraChange}
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="input-group">
                <label htmlFor="user_state">State</label>
                <select
                  id="user_state"
                  name="user_state"
                  value={extraInfo.user_state}
                  onChange={handleExtraChange}
                  required
                  disabled={!extraInfo.user_country}
                >
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="input-group">
                <label htmlFor="user_city">City</label>
                <select
                  id="user_city"
                  name="user_city"
                  value={extraInfo.user_city}
                  onChange={handleExtraChange}
                  required
                  disabled={!extraInfo.user_state}
                >
                  <option value="">Select City</option>
                  {cities.map((city, index) => (
                    <option key={index} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>
              
              {error && <p className="error-message">{error}</p>}
              {success && <p className="success-message">{success}</p>}
              <button 
                type="submit" 
                className="register-button"
                disabled={isExtraLoading}
              >
                {isExtraLoading ? (
                  <span className="button-spinner"></span>
                ) : (
                  "Complete Registration"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;